/**
 * API Configuration Constants
 * Centralize configuration for backend API connection
 */

// Backend API URL - will read from environment variables (Vite format)
const rawApiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
let API_BASE_URL = rawApiUrl.trim();
if (API_BASE_URL && !API_BASE_URL.endsWith('/api') && !API_BASE_URL.endsWith('/api/')) {
  API_BASE_URL = API_BASE_URL.endsWith('/') ? `${API_BASE_URL}api` : `${API_BASE_URL}/api`;
}

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
    CHANGE_EMAIL: '/auth/email',
  },
  
  // Users
  USERS: {
    LIST: '/users',
    GET: (id: string) => `/users/${id}`,
    CREATE: '/users',
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
  },
  
  // Doctors
  DOCTORS: {
    LIST: '/doctors',
    GET: (id: string) => `/doctors/${id}`,
    GET_BY_USER: (userId: string) => `/doctors/user/${userId}`,
    CREATE: '/doctors',
    UPDATE: (id: string) => `/doctors/${id}`,
    UPDATE_SCHEDULE: (id: string) => `/doctors/${id}/schedule`,
    UPDATE_OFFICE_ADDRESS: (id: string) => `/doctors/${id}/office-address`,
    GET_BY_SPECIALTY: (specialty: string) => `/doctors/specialty/${specialty}`,
    APPROVE: (id: string) => `/doctors/${id}/approve`,
  },
  
  // Patients
  PATIENTS: {
    LIST: '/patients',
    GET: (id: string) => `/patients/${id}`,
    GET_BY_USER: (userId: string) => `/patients/user/${userId}`,
    CREATE: '/patients',
    UPDATE: (id: string) => `/patients/${id}`,
    UPDATE_ADDRESS: (id: string) => `/patients/${id}/address`,
    GET_MEDICAL_HISTORY: (id: string) => `/patients/${id}/medical-history`,
    INITIAL_CHECKLIST_STATUS: '/patients/me/initial-checklist/status',
    COMPLETE_INITIAL_CHECKLIST: '/patients/me/initial-checklist/complete',
    /** Lista de pacientes vinculados ao médico autenticado (DOCTOR only) */
    MY_PATIENTS: '/patients/my-patients',
  },
  
  // Admins
  ADMINS: {
    LIST: '/admins',
    GET: (id: string) => `/admins/${id}`,
    GET_BY_USER: (userId: string) => `/admins/user/${userId}`,
    CREATE: '/admins',
    UPDATE: (id: string) => `/admins/${id}`,
    SUSPEND: (id: string) => `/admins/${id}/suspend`,
    REACTIVATE: (id: string) => `/admins/${id}/reactivate`,
  },
  
  // Appointments (quando implementado)
  APPOINTMENTS: {
    BASE: '/appointments',
    LIST: '/appointments',
    GET: (id: string) => `/appointments/${id}`,
    CREATE: '/appointments',
    UPDATE: (id: string) => `/appointments/${id}`,
    DELETE: (id: string) => `/appointments/${id}`,
    GET_BY_DOCTOR: (doctorId: string) => `/appointments/doctor/${doctorId}`,
    /** Agendamentos do médico autenticado (req.user.id) */
    GET_MY_DOCTOR: '/appointments/doctor',
    GET_BY_PATIENT: (patientId: string) => `/appointments/patient/${patientId}`,
    ACCEPT: (id: string) => `/appointments/${id}/accept`,
    REFUSE: (id: string) => `/appointments/${id}/refuse`,
    RESCHEDULE: (id: string) => `/appointments/${id}/reschedule`,
    APPROVE_RESCHEDULE: (id: string) => `/appointments/${id}/approve-reschedule`,
    REJECT_RESCHEDULE: (id: string) => `/appointments/${id}/reject-reschedule`,
  },
  
  // Documents (quando implementado)
  DOCUMENTS: {
    LIST: '/documents',
    GET: (id: string) => `/documents/${id}`,
    CREATE: '/documents',
    CONFIRM_MEMED: '/documents/memed',
    ACCESS: (id: string) => `/documents/${id}/access`,
    RENEWAL_BASE: (id: string) => `/documents/${id}/renewal-base`,
    DELETE: (id: string) => `/documents/${id}`,
    GET_BY_PATIENT: (patientId: string) => `/documents/patient/${patientId}`,
  },

  // Medical Records
  MEDICAL_RECORDS: {
    BASE: '/medical-records',
    SAVE: '/medical-records',
    BY_APPOINTMENT: (appointmentId: string) => `/medical-records/appointment/${appointmentId}`,
    PATIENT_SUMMARY: (patientId: string) => `/medical-records/patient/${patientId}/summary`,
    PATIENT_PDF: (patientId: string) => `/medical-records/patient/${patientId}/pdf`,
    PATIENT_HISTORY: (patientId: string) => `/medical-records/patient/${patientId}/history`,
    MY_DOCTORS: '/medical-records/my-doctors',
    ATTENDED_DOCTORS: '/medical-records/attended-doctors',
    MY_PATIENTS: '/medical-records/my-patients',
  },

  // Chat
  CHAT: {
    START_CONVERSATION: '/chat/start-conversation',
    MESSAGES: (appointmentId: string) => `/chat/${appointmentId}/messages`,
  },

  // Tracking GPS (médico em deslocamento → paciente)
  TRACKING: {
    LOCATION: (appointmentId: string) => `/tracking/${appointmentId}/location`,
    STREAM: (appointmentId: string) => `/tracking/${appointmentId}/stream`,
    END: (appointmentId: string) => `/tracking/${appointmentId}/end`,
  },

  // Templates (receitas, atestados, exames)
  TEMPLATES: {
    LIST: '/medical-records/templates',
    SAVE: '/medical-records/templates',
    DELETE: (id: string) => `/medical-records/templates/${id}`,
  },

  // Máscaras de Atendimento Clínico (anamnese/evolução)
  ATTENDANCE_TEMPLATES: {
    LIST: '/attendance-templates',
    CREATE: '/attendance-templates',
    GET: (id: string) => `/attendance-templates/${id}`,
    UPDATE: (id: string) => `/attendance-templates/${id}`,
    DELETE: (id: string) => `/attendance-templates/${id}`,
    DUPLICATE: (id: string) => `/attendance-templates/${id}/duplicate`,
    APPLY: (id: string) => `/attendance-templates/${id}/apply`,
  },

  // Admin Dashboard
  ADMIN_DASHBOARD: {
    STATS: '/admin/dashboard/stats',
    PENDING_APPROVALS: '/admin/dashboard/pending-approvals',
  },

  // Admin Analytics
  ADMIN_ANALYTICS: {
    OVERVIEW: '/admin/analytics/overview',
    APPOINTMENTS: '/admin/analytics/appointments',
    FINANCIAL: '/admin/analytics/financial',
    DOCTORS: '/admin/analytics/doctors',
    REPORTS: '/admin/analytics/reports',
    OPERATIONS: '/admin/analytics/operations',
  },

  // Audit and Moderation
  AUDIT: {
    LOGS: '/admin/audit/logs',
    PROCESS_OCCURRENCE: (id: string) => `/admin/audit/occurrences/${id}/process`,
  },

  // Admin User Management
  ADMIN_USERS: {
    SUSPEND: (id: string) => `/admins/users/${id}/suspend`,
    REACTIVATE: (id: string) => `/admins/users/${id}/reactivate`,
  },

  // Consultations
  CONSULTATIONS: {
    BASE: '/consultations',
    ON_DEMAND: '/consultations/on-demand',
    ON_DEMAND_CANCEL: '/consultations/on-demand/cancel',
  },

  // Payments (Mercado Pago)
  PAYMENTS: {
    STATUS: '/payments/status',
    PREFERENCE: '/payments/preference',
    GET: (id: string) => `/payments/${id}`,
  },

  // Memed (sessão/token via backend — nunca API key/secret no front)
  MEMED: {
    STATUS: '/memed/status',
    SESSION: '/memed/session',
  },
} as const;
